import * as XLSX from 'xlsx';

export default function useFileProcessing({ showNotification, EXCEL_READ_OPTIONS }) { // Removido THUMBNAIL_SAMPLE_SIZE del desestructuring
  
  const validateExcelFile = (selectedFile, allowedTypes) => {
    if (!selectedFile) return false;
    
    if (!allowedTypes.includes(selectedFile.type)) {
      showNotification('error', 'Formato no válido', 'El archivo seleccionado no es un archivo Excel válido.');
      return false;
    }
    
    return true;
  };
  
  const generateThumbnail = (selectedFile, callback) => {
    // Inicialmente mostramos un icono genérico
    const initialThumbnail = { 
      type: 'icon',
      icon: 'ri-file-excel-2-line'
    };
    
    callback(initialThumbnail);
    
    // Vamos a leer el archivo completo en lugar de solo una parte
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const fileData = new Uint8Array(e.target.result);
        const wb = XLSX.read(fileData, { 
          ...EXCEL_READ_OPTIONS,
          bookImages: true,
          cellText: false, // Reducir procesamiento
          cellNF: false,   // Reducir procesamiento
          cellDates: false // Reducir procesamiento
        });
        
        if (wb.SheetNames.length > 0) {
          const firstSheetName = wb.SheetNames[0];
          const worksheet = wb.Sheets[firstSheetName];
          
          try {
            // Convertimos una pequeña parte (primeras filas) a JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1,
              range: 0, // Solo leer las primeras filas
              defval: "" // Valor por defecto para celdas vacías
            });
            
            // Limitamos a 5 filas y 5 columnas máximo con validación
            const thumbnailData = jsonData
              .slice(0, 5)
              .map(row => {
                if (Array.isArray(row)) {
                  return row.slice(0, 5);
                }
                return [""];
              });
            
            // Guardamos los datos para la miniatura
            callback({
              type: 'data',
              data: thumbnailData,
              sheetCount: wb.SheetNames.length,
              rowCount: jsonData.length > 1 ? jsonData.length - 1 : 0 // Restamos la fila de encabezado
            });
          } catch (jsonError) {
            console.warn('Error al procesar datos JSON:', jsonError);
            // Mantenemos el icono genérico que ya establecimos
          }
        }
      } catch (error) {
        console.warn('Error al generar miniatura:', error);
        // En caso de error, mantenemos el icono genérico
      }
    };
    
    reader.onerror = (error) => {
      console.warn('Error de lectura:', error);
      // Ya tenemos un icono genérico establecido
    };
    
    // Leer el archivo completo en lugar de solo una parte
    reader.readAsArrayBuffer(selectedFile);
  };
  
  const processExcelFileData = (file, progressCallback) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          progressCallback(30); // Archivo leído
          const fileData = new Uint8Array(e.target.result);
          
          progressCallback(50); // Procesando archivo
          const workbookData = XLSX.read(fileData, EXCEL_READ_OPTIONS);
          
          progressCallback(60); // Archivo procesado
          
          // Obtener nombres de hojas
          const allSheets = workbookData.SheetNames;
          
          // Limitar a las 3 primeras hojas para evitar sobrecarga
          const limitedSheets = allSheets.slice(0, 3);
          
          progressCallback(70); // Procesando hojas
          
          resolve({
            workbook: workbookData,
            sheets: limitedSheets
          });
          
        } catch (error) {
          console.error('Error al procesar el archivo:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    });
  };
  
  const processDataInChunks = async (jsonData, chunkSize, chunkCallback) => {
    // Primero, filtramos todos los datos para obtener el conteo real
    const allFilteredData = [];
    
    // Filtramos todas las filas (excepto la primera que son encabezados)
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      // Filtrar filas que tienen al menos un valor no vacío y no solo espacios en blanco
      if (Array.isArray(row) && row.some(cell => {
        // Verificar que la celda no sea undefined, null, cadena vacía, o solo espacios
        if (cell === undefined || cell === null) {
          return false;
        }
        // Convertir a string y verificar que no sea solo espacios en blanco
        const cellStr = String(cell).trim();
        return cellStr !== '';
      })) {
        allFilteredData.push(row);
      }
    }
    
    // Ahora procesamos por chunks pero ya tenemos el total correcto
    const processChunk = async (startIndex, endIndex) => {
      const chunkData = allFilteredData.slice(startIndex, endIndex);
      
      // Llamamos al callback con los datos del chunk y el total real
      const continueProcessing = await chunkCallback(chunkData, allFilteredData.length);
      
      // Si hay más chunks por procesar y no se ha solicitado cancelación
      if (endIndex < allFilteredData.length && continueProcessing !== false) {
        // Pausar brevemente para permitir que la UI responda
        await new Promise(resolve => setTimeout(resolve, 0));
        return processChunk(endIndex, endIndex + chunkSize);
      } else {
        // Retornar todos los datos filtrados
        return allFilteredData;
      }
    };
    
    // Si no hay datos filtrados, llamamos al callback una vez con array vacío
    if (allFilteredData.length === 0) {
      await chunkCallback([], 0);
      return [];
    }
    
    // Iniciar el procesamiento por chunks
    return await processChunk(0, Math.min(chunkSize, allFilteredData.length));
  };
  
  return {
    validateExcelFile,
    generateThumbnail,
    processExcelFileData,
    processDataInChunks
  };
}